import { useEffect, useMemo } from 'react';
import { skipToken, useQuery } from '@apollo/client/react';

import { IUserListItemFragment, SkillReportUsersDocument } from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { usePagination } from 'common/hooks/usePagination';
import { useSkillUserFilter } from 'skill/hooks/useSkillUserFilter';

interface IUseSkillUserListOptions {
    skillIds: string[];
}

export const useSkillUserList = ({ skillIds }: IUseSkillUserListOptions) => {
    const { showApolloError } = useApolloError();

    const {
        filters,
        filterValues,
        filterValuesForQuery,
        isCompliant,
        loading: extraCategoriesLoading,
        onSearch,
        onFilterChange,
    } = useSkillUserFilter();
    const { paginationSettings, initializePagination, setPage } =
        usePagination(true);

    const paginationVariables = useMemo(
        () => ({
            offset: paginationSettings.offset,
            first: paginationSettings.first,
        }),
        [paginationSettings]
    );

    const userQueryVariables = useMemo(
        () => ({
            ...paginationVariables,
            ...filterValuesForQuery,
            skillIds,
        }),
        [paginationVariables, filterValuesForQuery, skillIds]
    );

    const {
        loading: usersLoading,
        data: usersData,
        error,
    } = useQuery(
        SkillReportUsersDocument,
        !!skillIds.length
            ? {
                  variables: {
                      ...userQueryVariables,
                  },
              }
            : skipToken
    );

    useEffect(() => {
        if (error) {
            showApolloError(error);
        }
    }, [error, showApolloError]);

    const usersObject = usersData?.skillReportUsers;
    const usersCount = usersObject?.count;
    const users = useMemo(
        () =>
            (usersObject?.edges
                .map((edge) => edge?.node || null)
                .filter(Boolean) as IUserListItemFragment[]) || [],
        [usersObject?.edges]
    );

    const loading = usersLoading || extraCategoriesLoading;

    useEffect(() => {
        if (loading || !initializePagination) return;

        initializePagination(usersCount || 0);
    }, [usersCount, loading, initializePagination]);

    return {
        users,
        usersCount,
        loading,
        paginationSettings,
        filters,
        filterValues,
        isCompliant,
        onSearch,
        onFilterChange,
        setPage,
    };
};
